import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getPlayerName, setPlayerName } from "@/lib/highscore";
import { getIsPro, getEmail } from "@/lib/pro";
import { detectNewlyUnlockedSkins } from "@/lib/skins";
import { Trophy, RotateCcw, Home, ListOrdered, Crown, Share2, Swords } from "lucide-react";
import { sfx } from "@/lib/sfx";
import PaywallDialog from "@/components/PaywallDialog";
import SkinUnlockToast from "@/components/SkinUnlockToast";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Draw a promo watermark over a snapshot dataURL and return a PNG Blob.
 * Adds the app's origin URL + a colored accent stripe so shares self-promote.
 */
async function watermarkSnapshot(dataUrl, roulette, score) {
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = dataUrl;
    });
    const w = img.width;
    const h = img.height;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    // Weather-driven accent color
    const accent =
      roulette?.type === "rain" ? "#7DD3FC" :
      roulette?.type === "fog" ? "#F0ABFC" :
      roulette?.type === "wind" ? "#FFD48A" : "#00F0FF";
    // Bottom-left corner stripe + text
    const barH = 44;
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(0, h - barH, w, barH);
    ctx.fillStyle = accent;
    ctx.fillRect(0, h - barH, 6, barH);
    ctx.font = "bold 16px 'JetBrains Mono', monospace";
    ctx.fillStyle = accent;
    ctx.textBaseline = "middle";
    ctx.fillText(
      `🎰 ${roulette?.type?.toUpperCase() || "ROULETTE"} · ${String(score).padStart(6, "0")}${roulette?.jackpot ? " · 5×" : ""}`,
      18,
      h - barH / 2
    );
    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = "12px 'JetBrains Mono', monospace";
    const origin = (window.location.origin || "").replace(/^https?:\/\//, "");
    ctx.fillText(origin, w - 14, h - barH / 2);
    return await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  } catch {
    return null;
  }
}

export default function GameOverDialog({ open, result, onRetry, onMenu, onLeaderboard }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [unlockedSkin, setUnlockedSkin] = useState(null);
  const isPro = getIsPro();

  useEffect(() => {
    if (open) {
      setName(getPlayerName() || "");
      setSubmitted(false);
      // After a run, check if the player crossed any skin-unlock threshold
      const newly = detectNewlyUnlockedSkins();
      if (newly.length > 0) setUnlockedSkin(newly[0]);
    }
  }, [open]);

  if (!result) return null;
  const { score, time, level, isHigh, won, replay } = result;

  const submit = async () => {
    const n = name.trim().slice(0, 20);
    if (!n) {
      toast.error(t("gameover.toast_name_missing"));
      return;
    }
    // Daily Challenge board is free for everyone; other levels require Pro.
    if (level !== "daily" && !isPro) {
      setPaywallOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      setPlayerName(n);
      await axios.post(`${API}/leaderboard`, {
        player_name: n,
        score,
        level,
        survival_time: time,
        email: getEmail() || undefined,
        replay: level === "daily" && replay && replay.length > 0 ? replay : undefined,
      });
      toast.success(t("gameover.toast_submitted"));
      setSubmitted(true);
    } catch (e) {
      toast.error(t("gameover.toast_submit_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        data-testid="gameover-dialog"
        className="max-w-md bg-black/75 backdrop-blur-2xl border border-white/10 text-white rounded-none"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-4xl tracking-tighter">
            {won ? (
              <span className="text-[#39FF14]" style={{ textShadow: "0 0 16px rgba(57,255,20,0.6)" }}>
                {t("gameover.escaped")}
              </span>
            ) : (
              <span className="text-[#FF0055]" style={{ textShadow: "0 0 16px rgba(255,0,85,0.6)" }}>
                {t("gameover.flattened")}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <Row k={t("gameover.level")} v={level.toUpperCase()} />
          <Row k={t("gameover.time")} v={`${time.toFixed(1)}s`} />
          <Row k={t("gameover.score")} v={String(score).padStart(6, "0")} accent="#00F0FF" big />
          {isHigh && (
            <div className="flex items-center gap-2 text-[#FFD400] text-xs tracking-[0.3em] pt-1" data-testid="new-highscore-badge">
              <Trophy size={14} /> {t("gameover.new_high")}
            </div>
          )}
        </div>

        {!submitted ? (
          <div className="mt-4 space-y-2">
            <div className="text-[10px] tracking-[0.3em] text-white/50 flex items-center gap-2">
              {t("gameover.submit_board")}
              {level === "daily" ? (
                <span className="text-[#39FF14]">{t("gameover.free_daily") || "· FREE FOR ALL"}</span>
              ) : (
                !isPro && <span className="text-[#FFD400] flex items-center gap-1"><Crown size={10} /> {t("menu.pro_tag")}</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="playername-input"
                placeholder={t("gameover.callsign_ph")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={20}
                className="bg-transparent border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-cyan-300 font-mono uppercase tracking-widest"
              />
              <button
                data-testid="submit-score-button"
                disabled={submitting}
                onClick={() => { sfx.click(); submit(); }}
                className={`px-4 py-2 border text-xs tracking-[0.3em] transition-colors disabled:opacity-40 ${
                  (level === "daily" || isPro)
                    ? "border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black"
                    : "border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black"
                }`}
              >
                {submitting ? "..." : (level === "daily" || isPro) ? t("gameover.send") : t("gameover.unlock")}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 text-xs text-[#39FF14] tracking-[0.3em]" data-testid="submitted-confirmation">
            {t("gameover.submitted")}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {result.roulette && result.won && (
            <button
              data-testid="share-roulette-button"
              onClick={async () => {
                sfx.click();
                const rou = result.roulette;
                const shareText =
                  `🎰 Parking Plot Roulette · ${rou.type.toUpperCase()}\n` +
                  `Score ${String(score).padStart(6, "0")} · ${time.toFixed(1)}s${rou.jackpot ? " · 5× JACKPOT" : " · 3×"}\n` +
                  `${rou.streak >= 2 ? `🔥 ${rou.streak} in a row — ` : ""}Spin yours: ${window.location.origin}`;
                try {
                  // Try native share with a watermarked snapshot first
                  if (result.snapshot && navigator.canShare && window.fetch) {
                    const blob = await watermarkSnapshot(result.snapshot, rou, score);
                    if (blob) {
                      const file = new File([blob], "roulette.png", { type: "image/png" });
                      if (navigator.canShare({ files: [file] })) {
                        await navigator.share({ title: "Parking Plot Roulette", text: shareText, files: [file] });
                        toast.success(t("gameover.toast_shared") || "Shared!");
                        return;
                      }
                    }
                  }
                  if (navigator.share) {
                    await navigator.share({ title: "Parking Plot Roulette", text: shareText });
                    toast.success(t("gameover.toast_shared") || "Shared!");
                  } else if (navigator.clipboard) {
                    await navigator.clipboard.writeText(shareText);
                    toast.success(t("gameover.toast_copied") || "Copied to clipboard");
                  } else {
                    toast.info("Sharing not supported on this device");
                  }
                } catch (e) {
                  // silent
                }
              }}
              className="flex items-center gap-2 px-4 py-2 border border-[#F0ABFC] text-[#F0ABFC] text-xs tracking-[0.3em] hover:bg-[#F0ABFC] hover:text-black transition-colors"
              style={{ boxShadow: "0 0 16px rgba(240,171,252,0.35)" }}
            >
              <Share2 size={14} /> SHARE ROULETTE{result.roulette.jackpot ? " · 5×" : ""}
            </button>
          )}
          {level === "daily" && (
            <button
              data-testid="share-daily-button"
              onClick={async () => {
                sfx.click();
                const today = new Date().toISOString().slice(0, 10);
                const shareText =
                  `Parking Plot Escape · Daily ${today}\n` +
                  `Score ${String(score).padStart(6, "0")} · ${time.toFixed(1)}s\n` +
                  `Same map for everyone today — beat me: ${window.location.origin}/play/daily`;
                try {
                  if (navigator.share) {
                    await navigator.share({ title: "Parking Plot Escape · Daily", text: shareText });
                    toast.success(t("gameover.toast_shared") || "Shared!");
                  } else if (navigator.clipboard) {
                    await navigator.clipboard.writeText(shareText);
                    toast.success(t("gameover.toast_copied") || "Copied to clipboard");
                  } else {
                    toast.info("Sharing not supported on this device");
                  }
                } catch (e) {
                  // User cancelled or blocked — silently ignore
                }
              }}
              className="flex items-center gap-2 px-4 py-2 border border-[#FFD400] text-[#FFD400] text-xs tracking-[0.3em] hover:bg-[#FFD400] hover:text-black transition-colors"
              style={{ boxShadow: "0 0 16px rgba(255,212,0,0.35)" }}
            >
              <Share2 size={14} /> {t("gameover.share") || "SHARE"}
            </button>
          )}
          {score > 0 && (
            <button
              data-testid="challenge-friend-button"
              onClick={async () => {
                sfx.click();
                // Build a challenge URL: /?duel_from=<name>&duel_score=<score>&duel_level=<level>
                const rawName = (getPlayerName() || "").trim().slice(0, 20) || "A rival";
                const origin = window.location.origin;
                const params = new URLSearchParams({
                  duel_from: rawName,
                  duel_score: String(score),
                  duel_level: String(level),
                });
                const challengeUrl = `${origin}/?${params.toString()}`;
                const shareText =
                  `🚗 I just scored ${score.toLocaleString()} on Parking Plot Escape${level ? ` (${String(level).toUpperCase()})` : ""}!\n` +
                  `Think you can beat me? Play here:\n${challengeUrl}`;
                try {
                  if (navigator.share) {
                    await navigator.share({ title: "Beat my Parking Plot Escape score", text: shareText, url: challengeUrl });
                    toast.success(t("gameover.toast_shared") || "Shared!");
                  } else if (navigator.clipboard) {
                    await navigator.clipboard.writeText(shareText);
                    toast.success(t("gameover.toast_copied") || "Copied · paste anywhere");
                  } else {
                    toast.info("Sharing not supported on this device");
                  }
                } catch {
                  // silent
                }
              }}
              className="flex items-center gap-2 px-4 py-2 border border-[#F0ABFC] text-[#F0ABFC] text-xs tracking-[0.3em] hover:bg-[#F0ABFC] hover:text-black transition-colors"
              style={{ boxShadow: "0 0 16px rgba(240,171,252,0.35)" }}
            >
              <Swords size={14} /> CHALLENGE A FRIEND
            </button>
          )}
          <button
            data-testid="retry-button"
            onClick={() => { sfx.click(); onRetry(); }}
            className="flex items-center gap-2 px-4 py-2 border border-white/30 text-xs tracking-[0.3em] hover:border-cyan-300 hover:text-cyan-300 transition-colors"
          >
            <RotateCcw size={14} /> {t("gameover.retry")}
          </button>
          <button
            data-testid="menu-button"
            onClick={() => { sfx.click(); onMenu(); }}
            className="flex items-center gap-2 px-4 py-2 border border-white/30 text-xs tracking-[0.3em] hover:border-white hover:text-white transition-colors"
          >
            <Home size={14} /> {t("gameover.menu")}
          </button>
          <button
            data-testid="see-leaderboard-button"
            onClick={() => { sfx.click(); onLeaderboard(); }}
            className="flex items-center gap-2 px-4 py-2 border border-white/30 text-xs tracking-[0.3em] hover:border-[#FF0055] hover:text-[#FF0055] transition-colors"
          >
            <ListOrdered size={14} /> {t("gameover.board")}
          </button>
        </div>
      </DialogContent>
      <PaywallDialog
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        feature={t("paywall.feature_leaderboard")}
      />
      {unlockedSkin && (
        <SkinUnlockToast skin={unlockedSkin} onClose={() => setUnlockedSkin(null)} />
      )}
    </Dialog>
  );
}

function Row({ k, v, accent, big }) {
  return (
    <div className="flex justify-between items-center border-b border-white/5 pb-2">
      <span className="text-[10px] tracking-[0.3em] text-white/50">{k}</span>
      <span
        className={`font-mono tabular-nums ${big ? "text-3xl font-bold" : "text-sm"}`}
        style={{ color: accent || "#f8f9fa", textShadow: accent ? `0 0 14px ${accent}80` : undefined }}
      >
        {v}
      </span>
    </div>
  );
}
